import { IsZhiHuiXingHe } from "@/core/hooks/SetTheme";
import { reactive, ref, Ref, getCurrentInstance, createVNode } from "vue";
import _ from "lodash";
import Cookies from "js-cookie";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { Modal, message } from "ant-design-vue";
import { ExclamationCircleFilled } from "@ant-design/icons-vue";
import { PaginationResp, ResponseResp } from "@/core/interface";
import { compileAxiosParams, clearCookies, getCurrentTenantId } from "@/utils";
import store from "@/store";
import Storage from "@/core/services/storage";
let lockr = new Storage("cpms_");
message.config({
  top: `100px`,
  duration: 1.5,
});
//需要格式化错误信息的code
const dontShowModalWhileRespCode = ["9999", "500", "0005"];
//以下code需要跳登录
const goLoginCode = ["0004", "0002", "0006", "0036", "0048"];
//需要重连的errror
const reconnectErrors = [
  "java.net.SocketException: Connection reset",
  "org.apache.http.NoHttpResponseException",
];

//超时异常
const timeoutErrors = ["CPMS-ADMIN-API timed-out and no fallback available"];
export class HttpService {
  static axios: AxiosInstance = HttpService.createAxios(process.env.VUE_APP_BASE_API);

  static backstageAxios: AxiosInstance = HttpService.createAxios(process.env.VUE_APP_BACK_API);

  static openSaasAxios: AxiosInstance = HttpService.createAxios(process.env.VUE_APP_OPEN_SAAS_API);

  static createAxios(baseApi) {
    const instance = axios.create({
      baseURL: baseApi,
      timeout: 60000,
    });

    instance.interceptors.request.use(
      (config) => {
        config.headers = config.headers || {};
        config.params = compileAxiosParams(config.params);

        const token = Cookies.get("at");
        if (token) {
          config.headers.access_token = token;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      (r) => {
        if (r.status === 200) {
          const res = r.data;
          if (goLoginCode.includes(res.respCode)) {
            if (res.respDesc !== "无权访问") {
              message.error(res.respDesc);
            }
            if (res.respCode !== "0036") {
              lockr.flush();
              clearCookies();
              setTimeout(() => {
                window.location.href = "/login";
              }, 1000);
            }
          }
        }
        return r;
      },
      (error) => {
        // console.error(error);
        return Promise.reject(error);
      }
    );
    return instance;
  }

  static usePagination<T>(api, options = {}) {
    const defaultOptions = {
      formatResult: (res) => res,
      onError: (err) => {},
      isOutError: false, //判断是否需要调用外部传进来的error处理
      onSuccess: () => {},
    };
    const { formatResult, onSuccess, onError, isOutError } = _.merge(defaultOptions, options);
    const loading = ref(false);
    const data: Ref<T[]> = ref([]);
    const paramsCache = ref();
    const pagination = reactive({
      current: 0,
      pageSize: 0,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `共 ${total} 条`,
      pageSizeOptions: ["10", "20", "50", "100"],
      reconnection: 0,
    });
    const run = async (...params): Promise<ResponseResp<PaginationResp<T>>> => {
      paramsCache.value = params;
      let output: ResponseResp<PaginationResp<T>> = {
        respCode: "",
        respDesc: "",
        data: {
          current: 0,
          size: 0,
          total: 0,
          records: [],
          filterTotalAmount: undefined,
          remark: undefined
        },
        reconnection: 0,
      };
      loading.value = true;
      try {
        const r: AxiosResponse<ResponseResp<PaginationResp<T>>> = await api(...params);

        if (r.status === 200) {
          const res = r.data;
          output = res;
          if (res.respCode === "0000") {
            const { current, size, total, records } = res.data;
            pagination.current = current;
            pagination.pageSize = size;
            pagination.total = total;
            data.value = formatResult(records);
            onSuccess();
          } else {
            console.log(2);
            if (!goLoginCode.includes(res.respCode)) {
              if (!dontShowModalWhileRespCode.includes(res.respCode)) {
                console.log(3);
                const errorMessage = res.respDesc || "未知错误";
                if (isOutError) {
                  HttpService.addExceptionLog(r);
                  onError(new Error(errorMessage));
                } else {
                  if (errorMessage.indexOf("error") >= 0) {
                    Modal.warning({
                      title: "提示",
                      icon: createVNode(ExclamationCircleFilled),
                      content: "网络原因导致系统异常，请重新刷新页面！",
                      zIndex: 1201,
                    });
                    HttpService.addExceptionLog(r);
                  } else {
                    Modal.warning({
                      title: "提示",
                      icon: createVNode(ExclamationCircleFilled),
                      content: errorMessage,
                      zIndex: 1201,
                    });
                  }
                }
              } else if (res.respCode === "9999" || res.respCode === "500") {
                if (isOutError) {
                  HttpService.addExceptionLog(r);
                  onError(new Error("服务异常"));
                } else {
                  if (reconnectErrors.some((o) => (res.respDesc || "").indexOf(o) >= 0)) {
                    if (pagination.reconnection < 3) {
                      pagination.reconnection++;
                      refresh();
                      console.log((r as any).config.url, `开始第${pagination.reconnection}次重连`);
                    }
                  } else {
                    Modal.warning({
                      title: "提示",
                      icon: createVNode(ExclamationCircleFilled),
                      content: "网络原因导致系统异常，请重新刷新页面！",
                      zIndex: 1201,
                    });
                    HttpService.addExceptionLog(r);
                  }
                }
              }
            }
          }
        }
      } catch (e: any) {
        onError(e);
      } finally {
        loading.value = false;
      }
      return output;
    };
    const refresh = async () => {
      if (paramsCache.value) {
        await run(...paramsCache.value);
      } else {
        await run();
      }
    };
    return { data, loading, pagination, run, refresh };
  }

  // TODO: 支持 cacheKey
  // TODO: 支持 cacheTime
  // 参考: https://cn.attojs.org/guide/documentation/cacheKey.html
  static useRequest<T>(api, options = {}) {
    const defaultOptions = {
      formatResult: (res) => res,
      onError: (err) => err,
      isOutError: false, //判断是否需要调用外部传进来的error处理
      onSuccess: (res) => res,
      successRespCode: ["0000"],
      setStore: (res) => res,
    };
    const { formatResult, onSuccess, onError, isOutError, successRespCode, setStore } = _.merge(
      defaultOptions,
      options
    );
    const loading = ref(false);
    const data: Ref<T | undefined> = ref();
    const paramsCache = ref();
    const run = async (...params): Promise<ResponseResp<T | undefined>> => {
      paramsCache.value = params;
      let output: ResponseResp<T | undefined> = {
        respCode: "",
        respDesc: "",
        data: undefined,
        reconnection: 0,
      };
      loading.value = true;

      try {
        const r: AxiosResponse<ResponseResp<T>> = await api(...params);
        if (r.status === 200) {
          const res = r.data;
          output = res;

          if (successRespCode.includes(res.respCode)) {
            data.value = formatResult(res.data);
            onSuccess(res.data);
            setStore(res.data);
          } else {
            if (!goLoginCode.includes(res.respCode)) {
              if (!dontShowModalWhileRespCode.includes(res.respCode)) {
                const errorMessage = res.respDesc || "未知错误";
                if (isOutError) {
                  HttpService.addExceptionLog(r);
                  onError(new Error(errorMessage));
                } else {
                  // Modal.error({ title: "错误", content: errorMessage });
                  if (errorMessage.indexOf("error") >= 0) {
                    Modal.warning({
                      title: "提示",
                      icon: createVNode(ExclamationCircleFilled),
                      centered: true,
                      content: "网络原因导致系统异常，请重新刷新页面！",
                      zIndex: 1201,
                    });
                    HttpService.addExceptionLog(r);
                  } else {
                    Modal.warning({
                      title: "提示",
                      icon: createVNode(ExclamationCircleFilled),
                      content: errorMessage,
                      centered: true,
                      zIndex: 1200,
                    });
                  }
                }
              } else if (res.respCode === "9999" || res.respCode === "500") {
                if (isOutError) {
                  HttpService.addExceptionLog(r);
                  onError(new Error("服务异常"));
                } else {
                  if (reconnectErrors.some((o) => (res.respDesc || "").indexOf(o) >= 0)) {
                    if (output.reconnection < 3) {
                      output.reconnection++;
                      refresh();
                      console.log((r as any).config.url, `开始第${output.reconnection}次重连`);
                    }
                  } else {
                    if (!timeoutErrors.some((o) => (res.respDesc || "").indexOf(o) >= 0)) {
                      Modal.warning({
                        title: "提示",
                        icon: createVNode(ExclamationCircleFilled),
                        content: "网络原因导致系统异常，请重新刷新页面！",
                        zIndex: 1201,
                      });
                      HttpService.addExceptionLog(r);
                    }
                  }
                }
              } else {
                const errorMessage = res.respDesc || "未知错误";
                Modal.warning({
                  title: "提示",
                  icon: createVNode(ExclamationCircleFilled),
                  content: errorMessage,
                  centered: true,
                  zIndex: 1201,
                });
                HttpService.addExceptionLog(r);
              }
            }
          }
        }
      } catch (e: any) {
        onError(e);
      } finally {
        loading.value = false;
      }
      return output;
    };
    const refresh = async () => {
      if (paramsCache.value) {
        await run(...paramsCache.value);
      } else {
        await run();
      }
    };
    return { data, loading, run, refresh };
  }

  static addExceptionLog = (r: any): void => {
    let data = {
      tenantId: getCurrentTenantId(),
      tenantUserId: (store?.state?.user?.userInfo as any)?.tenantUserId,
      params: JSON.stringify({
        params: r?.config?.params || "",
        data: r?.config?.data || "",
      }),
      path: r?.config?.url || "",
      respCode: r?.data?.respCode || "",
      respDesc: r?.data?.respDesc || "",
      pagePath: location.href,
      title: document?.title || "",
    };
    axios({
      method: "post",
      url: `${process.env.VUE_APP_BASE_API}/exception_log`,
      data,
    });
  };
}
